'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { Archive, getAllPublishedArchives } from '@/lib/db/archives';
import { createClient } from '@/lib/supabase/client';
import {
  BookMarked, MapPin, Users, Search, Loader2, ArrowRight, PlusCircle,
  SlidersHorizontal, FolderOpen, PenLine, Plus, X, AlertCircle,
  Link2, Sparkles, Music, Landmark, Feather, Layers, Star, Clock,
  Globe, ChevronRight, FileText, Flame,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type ProjectCategory =
  | 'family' | 'clan' | 'tribe' | 'custom' | 'culture' | 'music' | 'art' | 'event' | 'institution' | 'location' | 'myth';

interface DocProject {
  id: string;
  title: string;
  description: string;
  category: ProjectCategory;
  region: string | null;
  era_id: string | null;
  status: 'active' | 'completed' | 'archived';
  contributor_count: number;
  story_count: number;
  created_at: string;
  creator_name: string | null;
  creator_id: string;
  tags: string[];
}

interface NewProjectForm {
  title: string;
  description: string;
  category: ProjectCategory;
  region: string;
  era_id: string;
  tags: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_META: Record<ProjectCategory, { label: string; icon: React.ElementType; color: string; bg: string }> = {
  family:      { label: 'Family',      icon: Users,    color: '#E05A47', bg: '#E05A4714' },
  clan:        { label: 'Clan',        icon: Users,    color: '#7B6FA3', bg: '#7B6FA314' },
  tribe:       { label: 'Tribe',       icon: Globe,    color: '#2C5E3B', bg: '#2C5E3B14' },
  custom:      { label: 'Custom',      icon: Feather,  color: '#F39C12', bg: '#F39C1214' },
  culture:     { label: 'Culture',     icon: Layers,   color: '#008080', bg: '#00808014' },
  music:       { label: 'Music',       icon: Music,    color: '#E05A47', bg: '#E05A4714' },
  art:         { label: 'Art',         icon: Star,     color: '#F39C12', bg: '#F39C1214' },
  event:       { label: 'Event',       icon: Clock,    color: '#2C5E3B', bg: '#2C5E3B14' },
  institution: { label: 'Institution', icon: Landmark, color: '#7B6FA3', bg: '#7B6FA314' },
  location:    { label: 'Location',    icon: MapPin,   color: '#008080', bg: '#00808014' },
  myth:        { label: 'Myth',        icon: Sparkles, color: '#E05A47', bg: '#E05A4714' },
};

const ERA_LABELS: Record<string, string> = {
  'era-1': 'Early Iron Age',
  'era-2': 'Kingdom of Mutapa',
  'era-3': 'Rozvi Empire',
  'era-4': 'Colonial Contact',
  'era-5': 'Anti Colonial Resistance',
  'era-6': 'Nationalist Period',
  'era-7': 'Post-Independence',
};

const CATEGORIES = Object.keys(CATEGORY_META) as ProjectCategory[];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isNew(createdAt: string): boolean {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return new Date(createdAt) >= sevenDaysAgo;
}

// ─── Archive Card ─────────────────────────────────────────────────────────────

function ArchiveCard({ archive }: { archive: Archive }) {
  const publishedDate = archive.publishedAt
    ? new Date(archive.publishedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  return (
    <Link
      href={`/archives/${archive.id}`}
      className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 4px 24px rgba(193,122,62,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
      }}
    >
      <div className="h-1 w-full" style={{ backgroundColor: 'var(--primary)', opacity: 0.7 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <h3
          className="text-base font-semibold leading-snug line-clamp-2 group-hover:text-amber-500 transition-colors duration-150"
          style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
        >
          {archive.title}
        </h3>
        <p className="text-sm line-clamp-3 flex-1" style={{ color: 'var(--muted-foreground)' }}>
          {archive.description}
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {archive.region && (
            <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
              <MapPin size={11} />
              {archive.region}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Users size={11} />
            {archive.contributionCount} contribution{archive.contributionCount !== 1 ? 's' : ''}
          </span>
          {publishedDate && (
            <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
              {publishedDate}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: 'var(--border)' }}>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            by <span style={{ color: 'var(--foreground)' }}>{archive.creatorName}</span>
          </span>
          <ArrowRight
            size={14}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{ color: 'var(--primary)' }}
          />
        </div>
      </div>
    </Link>
  );
}

// ─── Project Card (mini) ──────────────────────────────────────────────────────

function ProjectCard({ project, onContribute }: { project: DocProject; onContribute: (id: string) => void }) {
  const meta = CATEGORY_META[project.category] || CATEGORY_META.custom;
  const CatIcon = meta.icon;
  const date = new Date(project.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const fresh = isNew(project.created_at);

  return (
    <div
      className="group rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5 flex flex-col relative"
      style={{ backgroundColor: 'var(--card)', borderColor: fresh ? meta.color + '55' : 'var(--border)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = meta.color + '88';
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 20px ${meta.color}18`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = fresh ? meta.color + '55' : 'var(--border)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      <div className="h-1 w-full" style={{ backgroundColor: meta.color, opacity: 0.7 }} />
      <div className="p-4 flex flex-col flex-1 gap-2.5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div
            className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            <CatIcon size={11} />
            {meta.label}
          </div>
          {fresh && (
            <span
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
              style={{ backgroundColor: '#E05A4718', color: '#E05A47', border: '1px solid #E05A4733' }}
            >
              <Flame size={10} />
              New
            </span>
          )}
        </div>

        {/* Title & description */}
        <div>
          <h3 className="text-sm font-bold leading-snug mb-1" style={{ color: 'var(--foreground)' }}>
            {project.title}
          </h3>
          <p className="text-xs leading-relaxed line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
            {project.description}
          </p>
        </div>

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {project.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="text-xs px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t mt-auto" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <span className="flex items-center gap-1">
              <Users size={10} />
              {project.contributor_count}
            </span>
            <span className="flex items-center gap-1">
              <FileText size={10} />
              {project.story_count} stories
            </span>
            {project.region && (
              <span className="flex items-center gap-1">
                <MapPin size={10} />
                {project.region}
              </span>
            )}
          </div>
          <button
            onClick={() => onContribute(project.id)}
            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full transition-all active:scale-95"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            Contribute
            <ChevronRight size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Project Modal ─────────────────────────────────────────────────────────

function NewProjectModal({
  onClose,
  onCreated,
  userId,
}: {
  onClose: () => void;
  onCreated: () => void;
  userId: string;
}) {
  const [form, setForm] = useState<NewProjectForm>({
    title: '',
    description: '',
    category: 'family',
    region: '',
    era_id: '',
    tags: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setSaving(true);
    setError(null);
    const supabase = createClient();
    const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
    const { error: dbError } = await supabase.from('doc_projects').insert({
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      region: form.region.trim() || null,
      era_id: form.era_id || null,
      creator_id: userId,
      tags,
      status: 'active',
      linked_timelines: [],
    });
    setSaving(false);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    onCreated();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div>
            <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Start a Documentation Project</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              Preserve a family, clan, tribe, custom, culture, music, art, event, institution, location, or myth
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm" style={{ backgroundColor: '#E05A4714', color: '#E05A47' }}>
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Category *</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {CATEGORIES.map((cat) => {
                const m = CATEGORY_META[cat];
                const CatIcon = m.icon;
                const isSelected = form.category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, category: cat }))}
                    className="flex flex-col items-center gap-1 p-2 rounded-lg border text-xs font-medium transition-all"
                    style={{
                      borderColor: isSelected ? m.color : 'var(--border)',
                      backgroundColor: isSelected ? m.bg : 'transparent',
                      color: isSelected ? m.color : 'var(--muted-foreground)',
                    }}
                  >
                    <CatIcon size={14} />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Project Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. The Ndlovu Family of Matabeleland"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What are you documenting? Why does it matter? What sources or knowledge do you have?"
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors resize-none"
              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Region & Era */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Region</label>
              <input
                type="text"
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                placeholder="e.g. Matabeleland"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
                style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Historical Era</label>
              <select
                value={form.era_id}
                onChange={(e) => setForm((f) => ({ ...f, era_id: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
                style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              >
                <option value="">Select era…</option>
                {Object.entries(ERA_LABELS).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Tags</label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="oral-tradition, Shona, pre-colonial (comma-separated)"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Separate tags with commas. These help connect your project to existing timelines.
            </p>
          </div>

          {/* Timeline note */}
          <div
            className="flex items-start gap-2.5 p-3 rounded-lg"
            style={{ backgroundColor: '#E05A4708', border: '1px solid #E05A4722' }}
          >
            <Link2 size={14} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 1 }} />
            <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Once your project is created, you can link it to existing timelines on the platform. Contributors can then attach their stories to your project and the connected timelines.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? 'Creating…' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function ArchivesPage() {
  const [archives, setArchives] = useState<Archive[]>([]);
  const [filtered, setFiltered] = useState<Archive[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { user } = useAuth();

  // Doc projects state
  const [projects, setProjects] = useState<DocProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);

  useEffect(() => {
    getAllPublishedArchives()
      .then((data) => {
        setArchives(data);
        setFiltered(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadProjects = async () => {
    setProjectsLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('doc_projects')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(6);

    if (!error && data) {
      const creatorIds = [...new Set(data.map((p: any) => p.creator_id).filter(Boolean))];
      let nameMap: Record<string, string> = {};
      if (creatorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', creatorIds as string[]);
        for (const p of profiles || []) {
          nameMap[p.id] = p.full_name || '';
        }
      }
      setProjects(
        data.map((p: any) => ({
          ...p,
          creator_name: nameMap[p.creator_id] || null,
          tags: p.tags || [],
          linked_timelines: p.linked_timelines || [],
          contributor_count: p.contributor_count || 0,
          story_count: p.story_count || 0,
        }))
      );
    }
    setProjectsLoading(false);
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFiltered(archives);
    } else {
      setFiltered(
        archives.filter(
          (a) =>
            a.title.toLowerCase().includes(q) ||
            a.description.toLowerCase().includes(q) ||
            (a.region || '').toLowerCase().includes(q) ||
            (a.creatorName || '').toLowerCase().includes(q)
        )
      );
    }
  }, [search, archives]);

  const handleContribute = (projectId: string) => {
    if (!user) {
      window.location.href = '/sign-up-login';
      return;
    }
    window.location.href = `/contribution-submission?project=${projectId}`;
  };

  const handleStartProject = () => {
    if (!user) {
      window.location.href = '/sign-up-login';
      return;
    }
    setShowNewProjectModal(true);
  };

  const newProjectCount = projects.filter((p) => isNew(p.created_at)).length;

  return (
    <AppLayout>
      <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-screen-xl mx-auto">
          {/* Page header */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-2">
              <BookMarked size={18} style={{ color: 'var(--primary)' }} />
              <span
                className="text-xs font-mono uppercase tracking-widest"
                style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
              >
                Public Archives
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
              <div>
                <h1
                  className="text-3xl sm:text-4xl font-bold mb-3"
                  style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
                >
                  Published Archives
                </h1>
                <p className="text-base max-w-2xl" style={{ color: 'var(--muted-foreground)' }}>
                  Explore curated collections of historical records, oral traditions, and sourced accounts. Registered users can contribute their published entries to any archive.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-auto">
                <Link
                  href="/archives/browse"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all duration-150"
                  style={{ backgroundColor: 'var(--card)', borderColor: 'var(--primary)', color: 'var(--primary)' }}
                >
                  <SlidersHorizontal size={15} />
                  Browse &amp; Filter
                </Link>
                <Link
                  href="/contribution-submission"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <PenLine size={15} />
                  Add a Story
                </Link>
                <button
                  onClick={handleStartProject}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 border"
                  style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  <FolderOpen size={15} />
                  Start a Project
                </button>
              </div>
            </div>
          </div>

          {/* ── New Projects from Users ─────────────────────────────────────── */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Flame size={16} style={{ color: '#E05A47' }} />
                <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                  New Projects from Contributors
                </h2>
                {newProjectCount > 0 && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{ backgroundColor: '#E05A4718', color: '#E05A47', border: '1px solid #E05A4733' }}
                  >
                    {newProjectCount} new this week
                  </span>
                )}
              </div>
              <Link
                href="/doc-projects"
                className="flex items-center gap-1 text-xs font-medium transition-colors"
                style={{ color: 'var(--primary)' }}
              >
                View all projects
                <ArrowRight size={13} />
              </Link>
            </div>

            {projectsLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={24} className="animate-spin" style={{ color: 'var(--muted-foreground)' }} />
              </div>
            ) : projects.length === 0 ? (
              <div
                className="rounded-xl border p-8 text-center"
                style={{ borderColor: 'var(--border)', borderStyle: 'dashed', backgroundColor: 'var(--card)' }}
              >
                <FolderOpen size={28} className="mx-auto mb-3" style={{ color: 'var(--muted-foreground)', opacity: 0.5 }} />
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>No projects yet</p>
                <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
                  Be the first to start a documentation project and preserve a piece of African history.
                </p>
                <button
                  onClick={handleStartProject}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all active:scale-95"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <Plus size={14} />
                  Start the first project
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => (
                  <ProjectCard key={project.id} project={project} onContribute={handleContribute} />
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
              Published Archives
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
          </div>

          {/* Search */}
          <div className="relative mb-8 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              type="text"
              placeholder="Search archives…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
              style={{
                backgroundColor: 'var(--card)',
                borderColor: 'var(--border)',
                color: 'var(--foreground)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Archives Content */}
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 size={32} className="animate-spin" style={{ color: 'var(--muted-foreground)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: '#C17A3E11', border: '1px dashed #C17A3E44' }}
              >
                <BookMarked size={36} style={{ color: 'var(--primary)', opacity: 0.5 }} />
              </div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
                {search ? 'No archives match your search' : 'No published archives yet'}
              </h2>
              <p className="text-sm max-w-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                {search
                  ? 'Try a different keyword or browse all archives.'
                  : 'Archives are curated collections of published contributions. Historians and curators can create them once contributions are published.'}
              </p>
              {search ? (
                <button
                  onClick={() => setSearch('')}
                  className="px-4 py-2 rounded-lg text-sm font-medium border"
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  Clear search
                </button>
              ) : (
                <Link
                  href="/contribution-submission"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <PlusCircle size={15} />
                  Submit a contribution
                </Link>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                {filtered.length} archive{filtered.length !== 1 ? 's' : ''} found
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((archive) => (
                  <ArchiveCard key={archive.id} archive={archive} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProjectModal && user && (
        <NewProjectModal
          userId={user.id}
          onClose={() => setShowNewProjectModal(false)}
          onCreated={loadProjects}
        />
      )}
    </AppLayout>
  );
}
